import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  Download,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, formatCurrency, getStatusColor } from '@utils';
import api from '@services/api';
import type { Order, ListResponse } from '@types';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

export default function OrdersListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders', { search: searchQuery, status: statusFilter, page: currentPage }],
    queryFn: async () => {
      return api.get<ListResponse<Order>>('/orders', {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
      });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            View and manage all ticket orders
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-secondary">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order number or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input"
            aria-label="Filter by Status"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="btn-secondary">
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card overflow-hidden">
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Order #</th>
                <th className="table-header-cell">Customer</th>
                <th className="table-header-cell">Event</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Total</th>
                <th className="table-header-cell">Date</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="table-row">
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="table-cell">
                        <div className="h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : orders?.data?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="empty-state">
                      <ShoppingCart className="empty-state-icon" />
                      <h3 className="empty-state-title">No orders yet</h3>
                      <p className="empty-state-description">
                        Orders will appear here when customers make purchases.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                orders?.data?.map((order: any, index: number) => (
                  <motion.tr
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="table-row"
                  >
                    <td className="table-cell font-medium">{order.orderNumber}</td>
                    <td className="table-cell">
                      <div>
                        <p className="font-medium">{order.buyerName}</p>
                        <p className="text-xs text-gray-500">{order.buyerEmail}</p>
                      </div>
                    </td>
                    <td className="table-cell">{order.event?.name}</td>
                    <td className="table-cell">
                      <span className={cn('badge', getStatusColor(order.status))}>
                        {order.status}
                      </span>
                    </td>
                    <td className="table-cell font-medium">
                      {formatCurrency(order.totalAmount, order.currency)}
                    </td>
                    <td className="table-cell">{formatDate(order.createdAt)}</td>
                    <td className="table-cell">
                      <Link
                        to={`/orders/${order.id}`}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex"
                        aria-label="View Order"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {orders?.meta && orders.meta.lastPage > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(orders.meta.currentPage - 1) * orders.meta.perPage + 1} to{' '}
              {Math.min(orders.meta.currentPage * orders.meta.perPage, orders.meta.total)} of{' '}
              {orders.meta.total} orders
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-secondary p-2"
                aria-label="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {orders.meta.lastPage}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(orders.meta.lastPage, p + 1))}
                disabled={currentPage === orders.meta.lastPage}
                className="btn-secondary p-2"
                aria-label="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
